import React, { useMemo } from 'react'
import DevicePanel from './DevicePanel'
import { deviceNames } from '../../utils/deviceDefinitions'
import './DeviceGrid.css'

// ES2022+: Constants moved outside component for better performance
const MEASUREMENT_ORDER = ['volts', 'amps', 'temp', 'field', 'motorRpm', 'soc', 'engineLoad']

// Device display order: Alternator, Battery, Regulator
const DEVICE_ORDER = ['alt1', 'alt2', 'bat1', 'bat2', 'regulator']

function DeviceGrid({ 
  data, 
  isLoading, 
  error, 
  getChartData, 
  canBusData = null,
  isChartVisible,
  hasRecentData,
  realTimeData = null,
  retryConnection = null,
  faults = [],
}) {

  // React 19: Simplified data processing with automatic optimization
  const dataByDevice = useMemo(() => {
    if (!data?.length) return {}
    return data.reduce((acc, row) => {
      acc[row.device] ??= []
      acc[row.device].push(row)
      return acc
    }, {})
  }, [data])

  // ES2022+: Simplified device data processing
  const processDeviceData = useMemo(() => (deviceData) => {
    const filtered = deviceData.filter(row => MEASUREMENT_ORDER.includes(row.measurement))
    return filtered.sort((a, b) => {
      const indexA = MEASUREMENT_ORDER.indexOf(a.measurement)
      const indexB = MEASUREMENT_ORDER.indexOf(b.measurement)
      return (indexA === -1 ? 1 : indexA) - (indexB === -1 ? 1 : indexB)
    })
  }, [])

  // Sort devices by the defined order: Alternator, Battery, Regulator
  const getSortedDevices = useMemo(() => {
    const deviceEntries = Object.entries(dataByDevice)
    return deviceEntries.sort(([deviceA], [deviceB]) => {
      const indexA = DEVICE_ORDER.indexOf(deviceA)
      const indexB = DEVICE_ORDER.indexOf(deviceB)
      // If device not in order list, put it at the end
      const orderA = indexA === -1 ? 999 : indexA
      const orderB = indexB === -1 ? 999 : indexB
      return orderA - orderB
    })
  }, [dataByDevice])

  // ES2022+: Early returns with simplified JSX
  if (isLoading) {
    return (
      <div className="text-center py-4">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading device data...</span>
        </div>
        <p className="mt-2 text-muted">
          <i className="bi bi-cloud-download me-2"></i>Loading device information...
        </p>
      </div>
    )
  }

  if (error) {
    return (
      <div className="alert alert-danger d-flex justify-content-between align-items-start">
        <div>
          <i className="bi bi-exclamation-triangle-fill me-2"></i>
          <strong>Error loading device data:</strong> {error}
        </div>
        {retryConnection && (
          <button className="btn btn-outline-danger btn-sm ms-2" onClick={retryConnection}>
            <i className="bi bi-arrow-clockwise me-1"></i>Retry
          </button>
        )}
      </div>
    )
  }

  if (!data?.length) {
    return (
      <div className="alert alert-info">
        <i className="bi bi-info-circle-fill me-2"></i>No device data available at this time.
      </div>
    )
  }
  return (
    <div className="device-grid container-fluid">
      {getSortedDevices.map(([device, deviceData]) => {
        const processedData = processDeviceData(deviceData)
        return processedData.length ? (
          <div key={device} className="device-section">
            <DevicePanel
              device={device}
              deviceName={deviceNames[device] || device}
              measurements={processedData}
              getChartData={getChartData}
              canBusData={canBusData}
              _isChartVisible={isChartVisible}
              _hasRecentData={hasRecentData}
              realTimeData={realTimeData}
              faults={faults}
            />
          </div>
        ) : null
      })}
    </div>
  )
}

export default React.memo(DeviceGrid)
